import { useContext } from 'react'

import { faEye } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Box, Button, Form, LoadingModule, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject, generateRedirectUrl } from '@domain-group/javascript-utils'

import useAgency from '@hooks/useAgency'
import useOrderItems from '@hooks/useOrderItems'
import useReduxAction from '@hooks/useReduxAction'

import ImpersonateEntityButton from '@components/ImpersonateEntityButton'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import { ModuleState } from '@redux/modules/module'

import { generateUrls } from '@hooks/useCampaign'

import InvoiceCampaignOrderItem from './InvoiceCampaignOrderItem'

const MODAL_KEY = 'ManageInvoiceCampaignModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageInvoiceCampaignModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaign, invoice } = modalPayload

  const { campaign_id } = invoice || {}

  const requestOptions = {
    include_campaign_orders: true,
    include_order_item_campaign_id: true,
    include_order_items: true,
  }

  useReduxAction(
    'campaigns',
    'loadCampaign',
    {
      entityId: campaign_id,
      ...requestOptions,
    },
    [campaign_id],
    {
      dispatchAction: (action, reqOptions) => action(campaign_id, reqOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!campaign_id && !loading,
    },
  )

  const orderItemsPayload = useOrderItems({
    filters: { campaign_id },
    requestOptions: { include_order_item_campaign_id: true },
  })

  const { filteredOrderItems, hasFilteredOrderItems, loading } = orderItemsPayload

  const agencyPayload = useAgency({ id: campaign?.agency_id })
  const {
    agency,
    callbacks: {
      loginAsAgency,
    },
  } = agencyPayload

  const { campaignEditDashboardUrl, editCampaignUrl } = generateUrls({ id: campaign_id })

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Campaign" titleSecondLine="Orders" />

      <SidebarModal.Content boxProps={{ paddingTop: 0 }}>
        <LoadingModule boxProps={{ marginTop: 'large' }} loading={loading} />

        <Box flexDirection="column">
          {!loading && hasFilteredOrderItems && (
            filteredOrderItems.map(orderItem => (
              <InvoiceCampaignOrderItem
                orderItem={orderItem}
                key={orderItem.id}
              />
            ))
          )}
        </Box>

        <Form.Field label="More Options">
          <ScopedConditionalContent permittedRoleTitles={['Provider Director']}>
            <Button
              buttonStyle="secondaryEdit"
              icon={<FontAwesomeIcon icon={faEye} />}
              size="medium"
              // @ts-expect-error TS2322
              width="auto"
              onClick={() => window.location.assign(`${editCampaignUrl}?redirect=${generateRedirectUrl()}`)}
            >
              View Campaign
            </Button>
          </ScopedConditionalContent>

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <ImpersonateEntityButton
              // @ts-expect-error TS2322
              callbacks={{ loginAsEntity: loginAsAgency }}
              icon={<FontAwesomeIcon icon={faEye} />}
              // @ts-expect-error TS2322
              entity={agency}
              marginRight="medium"
              redirectPath={campaignEditDashboardUrl}
              requestOptions={{
                redirect: `/agency/campaigns/${campaign_id}/edit`,
              }}
            >
              View Campaign
            </ImpersonateEntityButton>
          </ScopedConditionalContent>
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          size="large"
          onClick={() => closeModal()}
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageInvoiceCampaignModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
